import axios from 'axios';
import {
  convertToPageable,
  downloadFileWithPresignedUrl,
  extractData,
  handleApiException,
  handleResponse
} from '../helpers/apiResponseHelper';
import {
  CreateCustomerApplicationRequest,
  CustomerApplication,
  PagedCustomerApplicationsRequest,
  SignerType,
  UpdateCustomerApplicationRequest
} from '../models/customer-application';
import {apiService} from './api.service';
import { IPageResponse } from 'common/interfaces/IPageResponse';

/**
 * The signer travels as multipart: the JSON in a "request" part and the volikiri, when the user picked one, as
 * its own "powerOfAttorney" part. On update the file may be left out, which the server reads as "keep the
 * document already stored".
 */
function toSignerFormData(request: CreateCustomerApplicationRequest | UpdateCustomerApplicationRequest) {
    const signer = request.signer;
    const powerOfAttorney = signer && signer.type === SignerType.AUTHORIZED_REPRESENTATIVE
      ? signer.powerOfAttorney
      : undefined;

    // The file travels as its own multipart part, so it must not be serialized into the JSON part.
    const requestWithoutFile = powerOfAttorney
      ? { ...request, signer: { ...signer, powerOfAttorney: undefined } }
      : request;

    const formData = new FormData();
    formData.append('request', new Blob([JSON.stringify(requestWithoutFile)], { type: 'application/json' }));
    if (powerOfAttorney) formData.append('powerOfAttorney', powerOfAttorney);

    return formData;
}

async function create(request: CreateCustomerApplicationRequest): Promise<number> {
    return axios.post(apiService.getApiUrl() + '/customer-applications', toSignerFormData(request))
      .then(handleResponse).then(extractData).catch(error => handleApiException(error));
}

async function update(applicationId: number, request: UpdateCustomerApplicationRequest) {
    return axios.put(`${apiService.getApiUrl()}/customer-applications/${applicationId}`, toSignerFormData(request))
      .then(handleResponse).then(extractData).catch(error => handleApiException(error));
}

async function cancel(id: number, customerId: number, contactPersonId: number) {
    return axios.post(`${apiService.getApiUrl()}/customer-applications/${id}/cancel`, {}, {
      params: {
        customerId,
        contactPersonId
      }
    })
        .then(handleResponse)
        .then(extractData)
        .catch(error => handleApiException(error));
}

async function getPaged(
  customerId: number,
  request: PagedCustomerApplicationsRequest
) : Promise<IPageResponse<CustomerApplication>> {
  return axios
    .get(apiService.getApiUrl() + '/customer-applications/customer/' + customerId, {
      params: { ...request }
    })
    .then(handleResponse)
    .then(convertToPageable)
    .catch(handleApiException);
}

async function downloadPowerOfAttorney(applicationId: number, customerId: number) {
    return axios.get(`${apiService.getApiUrl()}/customer-applications/${applicationId}/power-of-attorney`, {
        params: { customerId }
    }).then(handleResponse).then(extractData)
      .then(fileUrl => downloadFileWithPresignedUrl(fileUrl))
      .catch(error => handleApiException(error));
}

const CustomerApplicationService = {
    create,
    update,
    cancel,
    getPaged,
    downloadPowerOfAttorney
}

export default CustomerApplicationService;
